<template>
  <div class="e-commerce-tab-wrapper">
    <div>
      <div class="store-setting">
        <div class="disable-overlay" v-if="!shopDetails.status"></div>
        <div class="flex flex-col">
          <div class="field status-toggler">
            <div class="label">
              <div class="flex gap-2 items-center">
                <label>Live</label>

                <div class="tool-tip">
                  <Tooltip
                    :popOverContent="liveLikTooltip"
                    popOverPosition="right"
                  />
                </div>
              </div>
            </div>
            <div class="form-control icon-control">
              <label class="toggle-button">
                <input
                  type="checkbox"
                  v-model="shopDetails.status"
                  @click="toggleEnableDisable($event)"
                />
                <span class="toggle-slider round"></span>
              </label>
            </div>
          </div>

          <div class="field">
            <div class="label">
              <div class="flex gap-2 items-center">
                <label>Favicon</label>
                <div class="tool-tip">
                  <Tooltip
                    popOverContent="
                            Favicon is a small 16x16 pixel icon
                            representing your e-commerce
                            webpage, displayed on tabs at the
                            top of a web browser."
                    popOverPosition="right"
                  />
                </div>
              </div>
              <span>Required</span>
              <small v-if="formErrors.logo" class="error-msg"
                >No image uploaded.</small
              >
            </div>
            <div class="form-control logo-upload">
              <div class="logo-section">
                <div class="portrait">
                  <img :src="faviconImage" />
                </div>
                <label for="favicon-upload" class="custom-file-upload">
                  Upload
                </label>
                <input
                  id="favicon-upload"
                  type="file"
                  @change="uploadFavicon"
                />
              </div>
            </div>
          </div>

          <!-- Logo Here -->
          <div class="field">
            <div class="label">
              <div class="flex gap-2 items-center">
                <label>Logo</label>
                <div class="tool-tip">
                  <Tooltip
                    popOverContent="
                    For best results, we recommend uploading image with Aspect Ratio 1:1."
                    popOverPosition="right"
                    icon="i"
                  />
                </div>
              </div>
              <span>Required</span>
              <small v-if="formErrors.logo" class="error-msg"
                >No image uploaded.</small
              >
            </div>
            <div class="form-control logo-upload">
              <div class="logo-section">
                <div class="portrait">
                  <img :src="logoImage" />
                </div>
                <label for="logo-upload" class="custom-file-upload">
                  Upload
                </label>
                <input id="logo-upload" type="file" @change="uploadLogo" />
              </div>
            </div>
          </div>

          <!-- Banner Here -->
          <div class="field">
            <div class="label">
              <div class="flex gap-2 items-center">
                <label>Banner</label>
                <div class="tool-tip">
                  <Tooltip
                    :popOverContent="bannerTooltip"
                    popOverPosition="right"
                    icon="i"
                  />
                </div>
              </div>
              <span>Required</span>
            </div>
            <div class="form-control">
              <div class="banner-container">
                <div class="banner-section">
                  <div class="banner-portrait"><img :src="bannerImage" /></div>
                  <label for="banner-upload" class="custom-file-upload">
                    Upload
                  </label>
                  <input
                    id="banner-upload"
                    type="file"
                    @change="handleUpload($event)"
                  />

                  <ImageCropper
                    :image="image"
                    :isOpen="isCropTool"
                    @closeModal="isCropTool = false"
                    @crop="file => uploadBanner(file)"
                    :aspectRatio="21 / 9"
                  />
                </div>
              </div>
              <p class="upload-info">
                Max file size: 5MB Dimensions: 1600 x 1050 (px)
              </p>
            </div>
          </div>

          <div class="field">
            <div class="label">
              <label>Page Title</label>
              <span>Required</span>
            </div>
            <div class="form-control">
              <div class="form-group">
                <input
                  v-model="shopDetails.title"
                  type="text"
                  placeholder="Enter store title"
                />
                <small v-if="formErrors.storeTitle" class="error-msg"
                  >This field is required.</small
                >
              </div>
            </div>
          </div>

          <div class="field">
            <div class="label">
              <label>Page Description</label>
              <span>Required</span>
            </div>
            <div class="form-control">
              <div class="store-descriptions">
                <div class="default-description description">
                  <div class="description-title">
                    <div class="option-selector">
                      <input
                        id="default"
                        v-model="shopDetails.showingDefaultDescription"
                        value="true"
                        type="radio"
                        name="description"
                        class="form-input"
                      />
                    </div>
                    <div class="option-label">
                      <label for="default" class="cursor-pointer"
                        >Default</label
                      >
                    </div>
                  </div>
                  <div class="description-value">
                    <p>{{ shopDetails.defaultDescription }}</p>
                  </div>
                </div>

                <div class="custom-description description">
                  <div class="description-title">
                    <div class="option-selector">
                      <input
                        id="custom"
                        v-model="shopDetails.showingDefaultDescription"
                        value="false"
                        type="radio"
                        name="description"
                        class="form-input"
                      />
                    </div>
                    <div class="option-label">
                      <label for="custom" class="cursor-pointer">Custom</label>
                    </div>
                  </div>
                  <div class="description-value w-full">
                    <textarea
                      v-model="shopDetails.description"
                      type="text"
                      placeholder="Add description*"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <div class="label">
              <div class="flex gap-2 items-center">
                <label>Show Out of Stock</label>
                <div class="tool-tip">
                  <Tooltip
                    popOverContent="
                    When turned on, products that are not in stock will be visible on your e-commerce Shop page."
                    popOverPosition="right"
                  />
                </div>
              </div>
              <span>Required</span>
            </div>
            <div class="form-control icon-control">
              <label class="toggle-button">
                <input v-model="shopDetails.outOfStock" type="checkbox" />
                <span class="toggle-slider round"></span>
              </label>
            </div>
          </div>

          <div class="field custom-domain">
            <div class="label">
              <label class="icon-label">
                Custom Domain
                <div class="tool-tip">
                  <Tooltip
                    popOverContent="Add an existing domain (eg.
                                shop.yourdomain.com) if you own one and connect it to your e-commerce store."
                    popOverPosition="right"
                  />
                </div>
              </label>
            </div>
            <div class="form-control">
              <div
                class="form-group"
                v-if="!shopCustomDomain || !customDomainSaved"
              >
                <div class="flex gap-10">
                  <input
                    v-model="shopDetails.customDomain"
                    type="text"
                    placeholder="Enter domain name"
                  />

                  <button
                    @click="addSubDomain"
                    class="whitebutton-gemnote button-gemnote-glass--tiny gray"
                  >
                    <span>Add</span>
                  </button>
                </div>
                <small
                  v-if="formErrors.customDomain.show"
                  class="error-msg"
                  v-html="formErrors.customDomain.message"
                />
              </div>

              <div v-else>
                <div v-if="!shopDnsStatus">
                  <div>
                    <div class="entered-domain">
                      <div class="domain-status">
                        <img src="../../assets/close-x.svg" alt="" />
                        <p>Not Connected</p>
                      </div>
                      <div class="custom-domain">
                        {{ shopDetails.customDomain }}
                      </div>
                      <div class="action">
                        <span @click="removeCustomDomain">x</span>
                      </div>
                    </div>
                  </div>

                  <div class="domain-link-info p-10">
                    <div class="icon">
                      <img src="../../assets/warning.svg" alt="" />
                    </div>
                    <div class="domain-link-info-content">
                      <div class="header">
                        <p>
                          Please visit the admin console of your domain
                          registrar and create one CNAME Record:
                        </p>
                      </div>
                      <div class="content">
                        <table>
                          <tr>
                            <th>Type</th>
                            <th>Name</th>
                            <th>Value</th>
                          </tr>
                          <tr>
                            <td>CNAME</td>
                            <td>{{ subDomain }}</td>
                            <td class="flex gap-3">
                              {{ proxyUrl }}
                              <span @click="copyProxyUrl">
                                <img
                                  src="../../assets/copy-icon.svg"
                                  class="copy-icon"
                                  alt=""
                              /></span>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div class="check-action">
                    <button @click="checkDNS">CHECK DNS STATUS</button>
                    <img src="../../assets/warning-black.svg" alt="" />
                    <p>
                      It may take up to a few hours for DNS changes to take
                      effect.
                    </p>
                  </div>
                </div>
                <div v-else>
                  <div>
                    <div class="entered-domain">
                      <div class="domain-status">
                        <img src="../../assets/check.svg" alt="" />
                        <p>Connected</p>
                      </div>
                      <div class="custom-domain">
                        {{ shopDetails.customDomain }}
                      </div>
                      <div class="action">
                        <span @click="removeCustomDomain">Remove</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- allowed domains here -->
          <section class="field custom-domain">
            <div style="margin-top:14px" class="label">
              <label class="icon-label">
                Allowed Domain(s)
                <div class="tool-tip">
                  <Tooltip
                    popOverContent="Enter web domain names to
                    restrict access to the store to only
                    emails from specific (allowed)
                    domains."
                    popOverPosition="right"
                  />
                </div>
              </label>
            </div>
            <div class="form-control">
              <div class="allow-domains flex gap-5">
                <input-tag
                  :class="{ 'updating-tags': updatingDomain }"
                  :validate="validateEmail"
                  placeholder="Enter domains"
                  v-model="allowed_domains"
                  ref="inputtag"
                  class="allow-domains-input"
                ></input-tag>
                <button
                  style="margin-left: 20px;"
                  @click="addAllowedDomain"
                  class="whitebutton-gemnote button-gemnote-glass--tiny allowed-domains gray"
                >
                  <span>Add</span>
                </button>
              </div>
            </div>
          </section>

          <!-- Allowed List Here -->
          <div class="background-content field">
            <div class="label">
              <p class="modified">
                Allow list

                <span>
                  <Tooltip
                    popOverContent="Upload an allowed list of emails that can have access to this redemption page."
                    popOverPosition="right"
                    icon="i"
                  />
                </span>
              </p>
            </div>
            <div
              :class="{
                'file-holder allow-list': EcommerceRecipientList.length
              }"
              class="form-control"
            >
              <section v-if="EcommerceRecipientList.length">
                <!-- Recipient List dropdown -->
                <div class="list-dropdown">
                  <MultipleRecipientListDropdown
                    :recipientList="EcommerceRecipientList"
                    placeholder="Select recipient list"
                    :isGroupOrder="true"
                    :isEcommerceRecipientList="true"
                    @handleSelection="data => (selectedLists = data)"
                  />
                </div>

                <!-- Pill Section Here -->
                <section v-if="selectedEcommerceRecipientList.length">
                  <div class="pill-holder">
                    <div
                      v-for="list in selectedEcommerceRecipientList"
                      :key="list.id"
                      class="list-pill"
                    >
                      <p :title="list.file_name">
                        {{ truncateText(list.file_name) }}
                      </p>

                      <span @click="removeList(list.id)" class="cursor-pointer">
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 8 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1.23438 1L6.67979 6.65854"
                            stroke="#24292C"
                          />
                          <path
                            d="M6.67969 1L1.23428 6.65854"
                            stroke="#24292C"
                          />
                        </svg>
                      </span>
                    </div>
                  </div>
                </section>

                <!-- Divider here -->
                <div class="justify-center flex">
                  <div class="divider ">
                    <p>or</p>
                  </div>
                </div>
              </section>

              <FileUpload
                id="allow-list"
                :fileSupport="['CSV']"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                @fileChange="e => uploadList(e)"
              />

              <!-- Allowed List Emails Here -->
              <div v-if="selectedEcommerceRecipientList.length">
                <AllowList
                  :groupOrderDetails="groupOrderDetails"
                  :shopDetails="shopDetails"
                  :isAllowListEcommerce="true"
                />
              </div>
            </div>
          </div>

          <div
            class="field"
            v-if="stripeInfoExist && !stripeErrors.show && shopDetails.status"
          >
            <div class="label self-center">
              <label>Site link</label>
            </div>
            <div class="form-control icon-control">
              <CopyBanner
                :text="shopDetails.storeLink ? shopDetails.storeLink : ''"
                @click="doCopy"
              />
            </div>
          </div>

          <div class="cta">
            <div class="btn-save">
              <button
                @click="updateStoreInfo"
                :disabled="disabledButton"
                class="button-gemnote button-gemnote--small"
              >
                <img
                  v-if="updatingStore"
                  src="../../assets/loading-white.svg"
                />
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from '@/internatinalization/i18n'
import {
  progressStatus,
  defaultImageUploadPlaceholder,
  status
} from '@/helpers'
import { mapState } from 'vuex'
import Tooltip from '../../components/Universal/ToolTipInfo.vue'
import CopyBanner from '@/components/Universal/CopyBanner.vue'
import ImageCropper from '@/components/Universal/ImageCropper'
import MultipleRecipientListDropdown from '@/components/SendGiftFlow/MultipleRecipientListDropdown.vue'
import AllowList from '../GroupOrder/AllowList.vue'
import FileUpload from '@/components/Universal/FileUpload'
import InputTag from 'vue-input-tag'
let bodyFormData = new FormData()
export default {
  name: 'EcommerceSetting',
  components: {
    Tooltip,
    CopyBanner,
    ImageCropper,
    MultipleRecipientListDropdown,
    AllowList,
    FileUpload,
    InputTag
  },
  data() {
    return {
      image: null,
      isCropTool: false,
      proxyUrl: process.env.VUE_APP_PROXY_URL,
      noImage: defaultImageUploadPlaceholder,
      showCustomDomainInfo: false,
      defaultLogo: '../../assets/default-logo.png',
      updatingStore: false,
      showStripeDetails: false,
      customDomainSaved: true,
      formErrors: {
        logo: false,
        storeTitle: false,
        customDomain: {
          message: 'This field is required.',
          show: false
        }
      },
      selectedLists: [],
      updatingDomain: false,
      allowed_domains: []
    }
  },
  computed: {
    disabledButton() {
      return !this.shopDetails.status || this.updatingStore
    },

    subDomain() {
      return this.shopDetails.customDomain.split('.')[0]
    },
    loadStatus() {
      return progressStatus(this.loadShopDetails)
    },
    stripeErrors() {
      if (this.shopDetails.stripeAccountBlob) {
        if (this.shopDetails.stripeAccountBlob.verification.disabledReason) {
          return {
            show: true,
            errors: this.shopDetails.stripeAccountBlob.verification.fieldsNeeded
              .length
              ? this.shopDetails.stripeAccountBlob.verification.fieldsNeeded
              : this.shopDetails.stripeAccountBlob.verification.errors
          }
        }
      }
      return {
        show: false,
        errors: []
      }
    },
    stripeInfoExist() {
      return !!this.shopDetails.stripeAccountBlob
    },
    canCreateLink() {
      if (this.shopDetails.stripeAccountBlob) {
        return !!this.shopDetails.stripeAccountBlob.verification.disabledReason
      }
      return false
    },
    liveLikTooltip() {
      return `<span>When turned on, your e-commerce store is live and can be accessed using the <b>Public Link.</b></span> `
    },
    logoImage() {
      return this.shopDetails.logoImage
        ? this.shopDetails.logoImage.url
        : require('../../assets/default-logo.png')
    },
    faviconImage() {
      return this.shopDetails.faviconImage
        ? this.shopDetails.faviconImage.url
        : require('../../assets/default-logo.png')
    },
    bannerImage() {
      return this.shopDetails.bannerImage
        ? this.shopDetails.bannerImage.url
        : require('../../assets/default-banner.png')
    },

    bannerTooltip() {
      return `<div class='flex flex-col gap-1'>
        <span>Maximum file size supported: 5MB.</span>
       <span>For best results, we recommend uploading image with Aspect Ratio 16:9.  </span>`
    },
    stripeButtonLoading() {
      return this.connectingStripe || this.creatingStore
    },
    showConnectStripe() {
      if (this.shopExist) {
        return true
      } else {
        return true
      }
    },
    stripeInfo() {
      return this.shopDetails.stripeAccountBlob
    },
    shopCustomDomain() {
      return this.$store.getters['ecommerce/hasCustomDomain']
    },
    shopDnsStatus() {
      return this.shopDetails.dnsStatus
    },
    shopExist() {
      return !!this.shopDetails.id
    },
    uploadComplete() {
      for (let key in this.shopDetails) {
        if (
          key === 'logoImage' ||
          key === 'faviconImage' ||
          key === 'bannerImage'
        ) {
          if (!this.shopDetails[key].url) {
            return false
          }
        }
      }
      return true
    },
    ...mapState({
      loadShopDetails: state => state.ecommerce.loadShopDetails,
      shopDetails: state => state.ecommerce.shopDetails,
      updatingShop: state => state.ecommerce.updatingShop,
      creatingStore: state => state.ecommerce.creatingStore,
      connectingStripe: state => state.ecommerce.connectingStripe,
      team: state => state.team.team,
      loadingCheckDnsStatus: state => state.ecommerce.loadingCheckDnsStatus,
      recipientList: state => state.recipient.recipientListRecords,
      EcommerceRecipientList: state =>
        state.recipient.recipientListEcommerceRecords,
      selectedRecipient: state => state.recipient.selectedRecipientList,
      groupOrderDetails: state => state.groupOrder.groupOrderDetails,
      allowListEcommerce: state => state.groupOrder.allowListEcommerce,
      selectedEcommerceRecipientList: state =>
        state.recipient.selectedEcommerceRecipientList
    })
  },
  created() {
    this.$store.dispatch('ecommerce/getStoreDetails').then(() => {
      if (
        this.shopDetails.allowedDomains &&
        this.shopDetails.allowedDomains.length
      ) {
        this.allowed_domains = this.shopDetails.allowedDomains
      }
      if (!this.shopDetails.customDomain) {
        this.customDomainSaved = false
      }
    })
  },
  mounted() {
    this.$store.dispatch('recipient/loadEcommerceRecipientList')
  },

  methods: {
    addAllowedDomain() {
      const domain = this.$refs.inputtag.$data.newTag
      const allowedDomainIndex = this.allowed_domains.findIndex(
        x => x === domain
      )
      if (allowedDomainIndex >= 0) {
        this.allowed_domains.splice(allowedDomainIndex, 1)
      }
      if (this.validateEmail(domain)) {
        this.allowed_domains.push(domain)
        this.$refs.inputtag.$data.newTag = ''
      }
    },
    validateEmail(value) {
      // eslint-disable-next-line no-useless-escape
      return /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
        value
      )
    },
    async checkStripe() {
      if (this.$route.query.account_id) {
        await this.$store.dispatch('ecommerce/updateStripe', {
          store_slug: this.shopDetails.slug,
          account_id: this.$route.query.account_id
        })
      }
    },
    doCopy() {
      this.$copyText(this.shopDetails.storeLink).then(
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'success',
            messages: [`Store link copied`]
          })
        },
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [`Can not copy store link.`]
          })
        }
      )
    },
    copyProxyUrl() {
      this.$copyText(this.proxyUrl).then(
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'success',
            messages: [`Proxy Url link copied`]
          })
        },
        () => {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [`Can not proxy url link.`]
          })
        }
      )
    },
    uploadLogo(e) {
      if (e.target.files.length > 0) {
        let size = e.target.files[0].size
        size = Math.round(size / 1024)

        if (size >= 2048) {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [i18n.t('validations.image-size-exceeded')]
          })
          return
        }
        bodyFormData.delete('e_commerce_store[logo_file]')
        bodyFormData.append('e_commerce_store[logo_file]', e.target.files[0])
        this.shopDetails.logoImage = {
          url: URL.createObjectURL(e.target.files[0])
        }
      }
    },
    uploadFavicon(e) {
      if (e.target.files.length > 0) {
        let size = e.target.files[0].size
        size = Math.round(size / 1024)

        if (size >= 2048) {
          this.$store.dispatch('notification/pushNotification', {
            type: 'error',
            messages: [i18n.t('validations.image-size-exceeded')]
          })
          return
        }
        bodyFormData.delete('e_commerce_store[favicon_file]')
        bodyFormData.append('e_commerce_store[favicon_file]', e.target.files[0])
        this.shopDetails.faviconImage = {
          url: URL.createObjectURL(e.target.files[0])
        }
      }
    },

    handleUpload(event) {
      this.image = event
      this.isCropTool = true
    },

    uploadBanner(blob) {
      const file = new File([blob], blob.name, { type: blob.type })

      console.log({ file })
      this.image = null
      bodyFormData.delete('e_commerce_store[banner_file]')
      bodyFormData.append('e_commerce_store[banner_file]', file)
      if (this.shopDetails.bannerImage !== null) {
        this.shopDetails.bannerImage.url = URL.createObjectURL(file)
      } else {
        this.shopDetails.bannerImage = {
          url: URL.createObjectURL(file)
        }
      }
    },
    async updateStoreInfo(shouldNotify = true) {
      this.updatingStore = true
      let newStoreLogo = bodyFormData.get('e_commerce_store[logo_file]')
      if (newStoreLogo === null) {
        bodyFormData.delete('e_commerce_store[logo_file]')
      }

      let newBanner = bodyFormData.get('e_commerce_store[banner_file]')
      if (newBanner === null) {
        bodyFormData.delete('e_commerce_store[banner_file]')
      }

      bodyFormData.set('e_commerce_store[title]', this.shopDetails.title)
      bodyFormData.set(
        'e_commerce_store[description]',
        this.shopDetails.description
      )
      bodyFormData.set(
        'e_commerce_store[primary_color]',
        this.shopDetails.primaryColor
          ? this.shopDetails.primaryColor
          : '#000000'
      )

      bodyFormData.set(
        'e_commerce_store[out_of_stock]',
        this.shopDetails.outOfStock
      )

      bodyFormData.set(
        'e_commerce_store[showingDefaultDescription]',
        Boolean(this.shopDetails.showingDefaultDescription)
      )
      if (this.shopDetails.customDomain === null) {
        bodyFormData.set('e_commerce_store[custom_domain]', '')
      } else {
        bodyFormData.set(
          'e_commerce_store[custom_domain]',
          this.shopDetails.customDomain
        )
      }
      if (this.allowed_domains.length === 0) {
        bodyFormData.set('e_commerce_store[allowed_domains][]', [])
      }
      if (this.allowed_domains.length !== 0) {
        bodyFormData.delete('e_commerce_store[allowed_domains][]')
      }
      this.allowed_domains.forEach(domain => {
        bodyFormData.append('e_commerce_store[allowed_domains][]', domain)
      })

      bodyFormData.append('shouldNotify', shouldNotify)
      this.updatingStore = false
      return this.$store.dispatch('ecommerce/editStore', bodyFormData)
    },
    toggleEnableDisable(e) {
      if (!this.uploadComplete && !this.shopDetails.status) {
        return this.handleToggleError(e)
      }

      if (this.shopExist) {
        this.$store.dispatch('ecommerce/enableDisableStore')
      }
    },

    handleToggleError(e) {
      this.$store.dispatch(
        'notification/pushNotification',
        {
          type: 'error',
          messages: [
            `Please upload all required images before enabling your store.`
          ]
        },
        { root: true }
      )((e.target.checked = false))
    },

    async addSubDomain() {
      let domainRegex = /^([a-zA-Z0-9][a-zA-Z0-9-_]*\.)*[a-zA-Z0-9]*[a-zA-Z0-9-_]*[[a-zA-Z0-9]+$/i

      if (!this.shopDetails.customDomain) {
        this.formErrors.customDomain.show = true
        this.formErrors.customDomain.message = 'This field is required.'
      } else if (!domainRegex.test(this.shopDetails.customDomain)) {
        this.formErrors.customDomain.show = true
        this.formErrors.customDomain.message = 'A valid domain is required'
      } else if (this.shopDetails.customDomain.split('.').length < 3) {
        this.formErrors.customDomain.show = true
        this.formErrors.customDomain.message =
          'Invalid entry. We currently don’t support root <br>' +
          'domains (ie. domain.com) so please enter a valid <br>' +
          'subdomain (ie. shop.yourdomain.com or \n' +
          'www.yourdomain.com).'
      } else {
        await this.updateStoreInfo(false).then(() => {
          this.$store.dispatch(
            'notification/pushNotification',
            {
              type: 'success',
              messages: ['Domain added successfully']
            },
            { root: true }
          )
        })
        this.customDomainSaved = true
      }
    },

    async removeCustomDomain() {
      this.shopDetails.customDomain = ''
      this.customDomainSaved = false
      await this.updateStoreInfo(false).then(() => {
        this.$store.dispatch(
          'notification/pushNotification',
          {
            type: 'success',
            messages: ['Domain removed successfully']
          },
          { root: true }
        )
      })
    },
    async connectStripe() {
      if (this.shopExist) {
        await this.updateStoreInfo()
        if (this.updatingShop !== status.ERROR) {
          await this.$store.dispatch('ecommerce/connectStripe')
        }
      } else {
        await this.createStore()
        if (this.creatingStore !== status.ERROR) {
          await this.$store.dispatch('ecommerce/connectStripe')
        }
      }
    },

    formIsValid() {
      let formIsValid = true
      let storeLogo = bodyFormData.get('e_commerce_store[logo_file]')

      let storeTitle = this.shopDetails.title

      if (storeLogo) {
        this.formErrors.logo = false
      } else {
        formIsValid = false
        this.formErrors.logo = true
      }

      if (storeTitle) {
        this.formErrors.storeTitle = false
      } else {
        formIsValid = false
        this.formErrors.storeTitle = true
      }

      return formIsValid
    },

    async createStore() {
      if (!this.formIsValid()) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: [i18n.t('ecommerce.action.create-store-validation-error')]
        })
        return
      }

      bodyFormData.set('e_commerce_store[paige_team_id]', this.team.id)
      bodyFormData.set('e_commerce_store[title]', this.shopDetails.title)
      bodyFormData.set(
        'e_commerce_store[description]',
        this.shopDetails.description
      )
      bodyFormData.set('e_commerce_store[primary_color]', '#000000')
      bodyFormData.set('e_commerce_store[secondary_color]', '#000000')
      bodyFormData.set(
        'e_commerce_store[out_of_stock]',
        this.shopDetails.outOfStock ? true : false
      )

      await this.$store.dispatch('ecommerce/createStore', bodyFormData)
    },
    async checkDNS() {
      await this.$store.dispatch('ecommerce/checkDNS', bodyFormData)
    },
    async uploadList(e) {
      const file = e.target.files[0]
      this.$store.dispatch('groupOrder/uploadAllowListEcommerce', {
        type: 'file',
        file: file
      })
    },
    tooltipPosition() {
      if (this.innerWidth < 1000) {
        return 'bottom-start'
      }
      return 'right'
    },
    removeList(id) {
      this.$store.dispatch('recipient/setEcommerceRecipent', {
        id,
        isEnable: false
      })
    },
    truncateText(string) {
      return string.length > 20 ? `${string.substring(0, 20)}…` : string
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/global.scss';
.allow-domains {
  display: flex;
}
.allow-domains-input {
  width: 220px;
}
.domain {
  display: flex;
  gap: 12px;
}
.domain-content {
  display: grid;
  grid-template-columns: 170px 1fr;
  margin-bottom: 2rem;
  grid-gap: 1rem;

  p {
    font-size: 0.813rem;
    margin-top: 0.8rem;
    text-align: left;
  }
}
.e-commerce-tab-wrapper {
  border-radius: 29px;
  padding: 3rem 4rem;
  background: inherit;
  border: 1px solid #000;
}

.button-gemnote-glass--tiny {
  padding: 0 1rem;
  width: auto;
}
.allowed-domains {
  margin-top: 12px;
}

input[type='radio'] {
  accent-color: #232220;
}

.description-value {
  p {
    font-size: 0.813rem;
  }
}

textarea {
  width: 60% !important;
  padding: 3px 0px !important;
}

.store-setting {
  text-align: left;
  background: $main-white;

  position: relative;
  z-index: 500;
  overflow: hidden;

  .status-toggler {
    position: relative;
    z-index: 5000;
  }
}

.field {
  display: flex;
  flex-direction: row;
  margin: 25px 0;

  .icon-control {
    display: flex;
    gap: 0.5rem;
  }

  .logo-upload {
    img {
      height: 65px;
    }
  }

  .banner-portrait {
    text-align: center;
    background: white;
    padding: 0px;
    border: 1px solid #979797;
    display: flex;

    img {
      width: 240px;
      height: auto;
    }
  }

  .label {
    width: 25%;
    text-align: left !important;
    display: flex;
    flex-direction: column;

    span {
      color: rgba(37, 40, 42, 0.5);
      font-size: 0.688rem;
      font-style: italic;
      font-weight: 100;
    }

    label {
      display: inline !important;
      text-align: left;
      font-size: 0.813rem;
    }
  }

  .form-control {
    width: 85%;
    text-align: left !important;

    input[type='text'],
    textarea {
      width: 220px;
      font-size: 0.813rem;
      color: #25282a;
      border: none;
      padding: 13px 0;
      border-radius: 0px;
      outline: none;
      -webkit-box-shadow: none;
      box-shadow: none;
      border-bottom: 1px solid #25282a;
      font-family: $inter;
      background: transparent;
    }
  }
}

.logo-section,
.banner-section {
  display: flex;
  align-items: end;
  gap: 1.5rem;
}

.custom-file-upload {
  font-style: normal;
  font-weight: normal;
  font-size: 0.813rem;
  line-height: 19px;
  text-decoration-line: underline;
  color: #24292c;
}

.upload-info {
  font-size: 10px;
  line-height: 14px;
  color: rgba(36, 41, 44, 0.5);
}

input[type='text'] {
  width: 300px;
}

.store-descriptions {
  .description {
    display: flex;
    flex-direction: row;
    gap: 2em;
    align-items: flex-start;

    .description-title {
      .option-selector {
        display: flex;

        input {
          height: 16px;
          width: 16px;
          border: 2px solid #25282a !important;
          color: #25282a;
        }
      }
    }
  }

  .custom-description {
    margin-top: 30px;
  }

  .description-title {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }
}

.cta {
  text-align: right;
  margin-top: 70px;

  .button-gemnote--small {
    width: auto !important;
    padding: 0 1.5rem;
    height: 2.5rem;
    display: flex;
    margin-left: auto;
    gap: 20px;
  }
}

.connect-stripe {
  text-transform: unset;
  margin-top: 32px;
  width: 9.5rem;
  display: flex;
  border-radius: 4px;
  padding: 10px 20px;
  background-color: #635bfe;
  border: #635bfe;
  font-weight: lighter;

  span {
    font-size: 0.813rem;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover,
  &:focus {
    background-color: #635bfe;
    border: #635bfe;
  }
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 20px;

  span {
    font-size: 0.813rem;
  }
}

.account-status {
  margin-bottom: 30px;
  display: flex;
  gap: 20px;

  .details-toggle {
    span {
      text-decoration: underline;
      font-size: 0.688rem;
      cursor: pointer;
    }
  }
}

.stripe-details {
  display: flex;
  border: 0.5px solid #232220;
  width: 550px;
  margin: 20px 0;

  & > div {
    width: 50%;
    padding: 20px;

    .row {
      display: flex;
      gap: 10px;
      padding: 5px 0;
      height: unset !important;

      & > div:first-child {
        width: 50%;
        font-weight: 700;
      }

      & > div {
        width: 50%;
        font-size: 0.688rem;
        color: #7b7a7a;
        font-weight: 400;
        text-align: left;
        line-break: anywhere;
      }
    }
  }
}

.error-msg {
  color: #da5737;
  font-size: 10px;
  line-height: 14px;
  display: block;
}

.button-gemnote:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.stripe-errors {
  background: $main-white;
  padding: 0 1.5rem;

  p {
    padding-top: 24px;
    font-size: 0.875rem;
    display: flex;
    gap: 10px;
  }

  ul {
    padding-bottom: 24px;
    margin-left: 20px;
    margin-top: 20px;
    text-transform: capitalize;

    li {
      font-size: 0.688rem;
      color: #da5737;
      padding: 5px;
      margin: 2px;
      line-height: normal;
    }
  }
}

.link-content {
  display: flex;
  justify-content: flex-start;
  width: 100%;

  span {
    border: 1px solid $onyx;
    text-align: justify;
    padding: 0.5rem 0.5rem;
  }

  div {
    min-width: 50px;
    border-top: 1px solid $onyx;
    border-bottom: 1px solid $onyx;
    border-right: 1px solid $onyx;
    display: flex;
    justify-content: center;

    img {
      cursor: pointer;
      width: 1.2rem;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.label {
  font-weight: bold;
}

button {
  height: 52px;
}

.add-domain {
  padding: 0px 20px;
  height: unset;
}

.entered-domain {
  display: flex;
  font-weight: 400;
  font-size: 14px;
  gap: 15px;
  padding-bottom: 15px;

  .custom-domain {
    font-weight: 700;
  }

  .action span {
    cursor: pointer;
  }
}

.domain-status {
  padding-bottom: 15px;
  display: flex;
  flex-direction: row;
  gap: 10px;

  p {
    font-size: 14px;
  }

  img {
    width: 10px;
  }
}

.check-action {
  padding-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  p {
    font-size: 12px;
  }

  button {
    font-size: 14px;
    font-weight: 700;
    padding: 5px 35px !important;
    border: 1px solid #24292c;
    border-radius: 40px;
    line-height: unset;
    height: unset !important;
    white-space: nowrap;
    background: transparent;
  }
}

.domain-link-info {
  background: inherit;
  padding: 20px;
  display: flex;
  flex-direction: row;
  gap: 5px;
  border: 1px solid #232220;

  .icon {
    padding-top: 4px;
    width: 40px;
  }

  .domain-link-info-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .header {
    display: flex;

    p {
      font-size: 14px;
    }
  }

  .content {
    p {
      font-size: 12px;
    }

    table {
      margin-top: 10px;
      width: 100%;

      td,
      th {
        padding: 10px 25px;
        padding-left: 0px;
      }

      td {
        font-weight: 700;
        font-size: 12px;

        .copy-icon {
          width: 12px;
          height: 100%;
          cursor: pointer;
        }
      }

      th {
        font-size: 12px;
        line-height: 16px;
        color: #7b7a7a;
      }
    }
  }
}

.field .label label {
  display: flex !important;
  flex-direction: row !important;
  gap: 15px;
}

.background-content {
  // display: grid;
  // grid-gap: 1rem;
  // grid-template-columns: 101px 1fr;
  // margin-bottom: 2rem;

  p {
    text-align: left;
    font-weight: bold;
    align-self: start;
    font-size: $content-font-size;

    &.allowed-files {
      color: rgba(36, 41, 44, 0.5);
      font-size: 10px;
      padding-top: 3px;
      margin-left: 1.25rem;
      font-weight: normal;
      align-self: center;
    }

    &.modified {
      display: flex;
      padding-top: 1.125rem;

      &.background {
        padding-top: 0;
      }

      span {
        display: block;
        margin-left: 5px;
        position: relative;
        bottom: 1px;
      }
    }
  }

  .banner-portrait {
    height: 100px;
    width: 231px;
    height: 150px;
    border: 1px solid $main-4;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      margin: 0 auto;
    }
  }

  .file-name {
    cursor: pointer;
    color: #24292c;
    font-weight: bold;
    font-size: 0.813rem;
    margin-top: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .banner-section {
    display: flex;
    align-items: flex-end;
    gap: 1rem;
  }

  .file-holder {
    border: 0.5px solid #979797;
    height: 88px;
    padding: 0.375rem 1rem;

    &.allow-list {
      height: auto;
      padding: 0.75rem 1rem;
    }

    &.none {
      border: none;
    }

    .divider {
      color: #25282a;
      margin: 1.25rem 0;
      font-size: 0.875rem;
      position: relative;
      width: 300px;
      height: 50px;
      line-height: 50px;
      text-align: center;

      &::before,
      &::after {
        position: absolute;
        width: 130px;
        height: 1px;
        top: 25px;
        background-color: #979797;
        content: '';
      }

      &::before {
        left: 0;
      }

      &::after {
        right: 0;
      }

      p {
        text-align: center;
      }
    }
  }
}

.pill-holder {
  margin-top: 1.125rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;

  .list-pill {
    background: #e6e4e1;
    border-radius: 11.6px;
    display: flex;
    align-items: center;
    padding: 0.313rem 0.75rem;
    width: auto;
    max-width: 150px;

    p {
      color: #24292c;
      font-size: 0.625rem;
      margin-right: 0.5rem;
      font-weight: 400;
    }
  }
}
</style>
